// ========================================
// UTILIDADES PARA FIRMA DIGITAL
// ========================================

/**
 * Convierte dataURL (base64) a Blob para envío mediante FormData
 * 
 * @param dataURL - String en formato "data:image/png;base64,iVBORw0KG..."
 * @returns Blob con el contenido de la imagen
 * 
 * @example
 * const dataURL = signaturePad.toDataURL('image/png');
 * const blob = dataURLtoBlob(dataURL);
 * formData.append('signature', blob, 'firma.png');
 */
export function dataURLtoBlob(dataURL: string): Blob {
  // Separar el header (data:image/png;base64) del contenido base64
  const arr = dataURL.split(',');
  
  // Extraer el MIME type (image/png, image/jpeg, etc.)
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  
  // Decodificar base64 a string binario
  const bstr = atob(arr[1]);
  
  // Convertir string binario a array de bytes
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  // Crear Blob con el tipo MIME correcto
  return new Blob([u8arr], { type: mime });
}

/**
 * Valida que un dataURL sea una imagen válida
 * 
 * @param dataURL - String a validar
 * @returns true si es un dataURL válido de imagen
 */
export function isValidImageDataURL(dataURL: string): boolean {
  if (!dataURL || typeof dataURL !== 'string') {
    return false;
  }
  
  // Verificar formato básico
  const imageRegex = /^data:image\/(png|jpeg|jpg|svg\+xml);base64,/;
  return imageRegex.test(dataURL);
}

/**
 * Estima el tamaño en bytes de un dataURL
 * 
 * @param dataURL - DataURL de la imagen
 * @returns Tamaño aproximado en bytes
 */
export function estimateDataURLSize(dataURL: string): number {
  if (!dataURL) return 0;
  
  // El tamaño base64 es ~33% mayor que el binario
  // Restamos el header "data:image/png;base64,"
  const base64Length = dataURL.split(',')[1]?.length || 0;
  
  // Fórmula: (base64Length * 3) / 4
  return Math.ceil((base64Length * 3) / 4);
}

/**
 * Verifica si el tamaño de la firma excede el límite
 * 
 * @param dataURL - DataURL de la firma
 * @param maxSizeMB - Tamaño máximo en MB (default: 2)
 * @returns true si excede el límite
 */
export function exceedsMaxSize(dataURL: string, maxSizeMB: number = 2): boolean {
  const sizeBytes = estimateDataURLSize(dataURL);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  return sizeBytes > maxSizeBytes;
}
